import React, {useEffect, useState} from "react";
import axios from "axios";
import {toast} from "react-toastify";
import {Box, Button, Column, Columns, Input, Modal, Section, Select, Title} from "@allxsmith/bestax-bulma";
import {printZpl} from "./labelPrinter";

// Labels are ZPL for the Zebra ZD230 - the printer + format FedEx certified.
// Printing goes to the site's network Zebra through the ACS Label Printer Helper.

const BLANK_BOX = {weight: 2, length: 30, width: 20, height: 10};
const LINE_MAX = 35;   // FedEx limit per address line / city

export default function FedexLabelModal({open, setOpen, partOrder, replace, onBooked}) {
    const [info, setInfo] = useState(null);
    const [form, setForm] = useState(null);
    const [step, setStep] = useState("form");   // form -> confirm -> done
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [result, setResult] = useState(null);

    useEffect(() => {
        if (!open || !partOrder.id) return;
        setStep("form");
        setError("");
        setResult(null);
        axios.get("/api/partorder/" + partOrder.id + "/fedex-label")
            .then(r => {
                setInfo(r.data);
                setForm(r.data.prefill);
            })
            .catch(() => setError("Could not load the delivery details"));
    }, [open, partOrder.id]);

    const close = () => setOpen(false);
    const set = (k, v) => setForm({...form, [k]: v});
    const setBox = (i, k, v) => {
        const boxes = form.boxes.map((b, j) => j === i ? {...b, [k]: v} : b);
        setForm({...form, boxes});
    };
    const addBox = () => setForm({...form, boxes: [...form.boxes, {...BLANK_BOX}]});
    const removeBox = (i) => setForm({...form, boxes: form.boxes.filter((b, j) => j !== i)});

    const missing = () => {
        if (!form) return "Loading";
        const req = {name: "Name", street: "Address line 1", city: "Town / city", postcode: "Postcode", phone: "Phone"};
        const gaps = Object.keys(req).filter(k => !String(form[k] || "").trim()).map(k => req[k]);
        ["street", "street2", "city"].forEach(k => {
            if (String(form[k] || "").trim().length > LINE_MAX) gaps.push((req[k] || "Address line 2") + " over " + LINE_MAX + " characters");
        });
        if (!form.boxes.length) gaps.push("at least one box");
        if (form.boxes.some(b => !(parseFloat(b.weight) > 0))) gaps.push("a weight for every box");
        return gaps.length ? "Please fix: " + gaps.join(", ") : "";
    };

    const book = () => {
        setBusy(true);
        setError("");
        axios.post("/api/partorder/" + partOrder.id + "/fedex-label", {form, confirm: true, replace: !!replace})
            .then(async r => {
                const d = r.data;
                const printed = await printZpl(d.zpl, "fedex_" + d.fedex_tracking + ".zpl");
                setResult({...d, printed});
                setStep("done");
                onBooked(d);
            })
            .catch(err => {
                setError((err.response && err.response.data && err.response.data.detail) || "FedEx label failed");
                setStep("form");
            })
            .finally(() => setBusy(false));
    };

    const serviceLabel = (v) => ((info && info.services.find(s => s.value === v)) || {label: v}).label;
    const totalKg = form ? form.boxes.reduce((t, b) => t + (parseFloat(b.weight) || 0), 0) : 0;

    return (
        <Modal onClose={busy ? undefined : close} active={open}>
            <Modal.Content>
                <Section style={{background: "white"}}>
                    <Title>{replace ? "Book a replacement FedEx label" : "Create FedEx label"}</Title>
                    {info && info.sandbox ?
                        <p className="notification is-info is-light">TEST SYSTEM - labels are booked in FedEx's test environment. Nothing is shipped or charged.</p>
                        : null}
                    {error ? <p className="notification is-danger is-light">{error}</p> : null}

                    {!form ? <p>{error ? "" : "Loading..."}</p> : null}

                    {form && step === "form" ?
                        <>
                            <Columns isMultiline>
                                <Column size={6}>
                                    <label className="label">Name</label>
                                    <Input value={form.name || ""} onChange={e => set("name", e.target.value)}/>
                                </Column>
                                <Column size={6}>
                                    <label className="label">Company</label>
                                    <Input value={form.company || ""} onChange={e => set("company", e.target.value)}/>
                                </Column>
                                <Column size={6}>
                                    <label className="label">Address line 1</label>
                                    <Input value={form.street || ""} onChange={e => set("street", e.target.value)}/>
                                    <p className={"help" + ((form.street || "").length > LINE_MAX ? " is-danger" : "")}>{(form.street || "").length}/{LINE_MAX}</p>
                                </Column>
                                <Column size={6}>
                                    <label className="label">Address line 2</label>
                                    <Input value={form.street2 || ""} onChange={e => set("street2", e.target.value)}/>
                                    <p className={"help" + ((form.street2 || "").length > LINE_MAX ? " is-danger" : "")}>{(form.street2 || "").length}/{LINE_MAX}</p>
                                </Column>
                                <Column size={4}>
                                    <label className="label">Town / city</label>
                                    <Input value={form.city || ""} onChange={e => set("city", e.target.value)}/>
                                    <p className={"help" + ((form.city || "").length > LINE_MAX ? " is-danger" : "")}>{(form.city || "").length}/{LINE_MAX}</p>
                                </Column>
                                <Column size={4}>
                                    <label className="label">Postcode</label>
                                    <Input value={form.postcode || ""} onChange={e => set("postcode", e.target.value.toUpperCase())}/>
                                </Column>
                                <Column size={4}>
                                    <label className="label">Phone (FedEx requires one)</label>
                                    <Input value={form.phone || ""} onChange={e => set("phone", e.target.value)}/>
                                </Column>
                                <Column size={6}>
                                    <label className="label">Service</label>
                                    <Select value={form.service_type} onChange={e => set("service_type", e.target.value)}>
                                        {info.services.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                                    </Select>
                                </Column>
                            </Columns>

                            <label className="label">Boxes - one label prints per box</label>
                            {form.boxes.map((b, i) =>
                                <Columns key={i} isMultiline>
                                    <Column size={1}><p style={{paddingTop: "8px"}}>Box {i + 1}</p></Column>
                                    <Column size={2}>
                                        <Input type="number" min={0.1} step={0.1} value={b.weight} placeholder="kg"
                                               onChange={e => setBox(i, "weight", e.target.value)}/>
                                        <p className="help">kg</p>
                                    </Column>
                                    {["length", "width", "height"].map(k =>
                                        <Column size={2} key={k}>
                                            <Input type="number" min={1} value={b[k]}
                                                   onChange={e => setBox(i, k, e.target.value)}/>
                                            <p className="help">{k} cm</p>
                                        </Column>
                                    )}
                                    <Column size={1}>
                                        {form.boxes.length > 1 ?
                                            <Button color="danger" isOutlined onClick={() => removeBox(i)}>Remove</Button> : null}
                                    </Column>
                                </Columns>
                            )}
                            <Button color="light" onClick={addBox}>+ Add box</Button>

                            <Columns style={{marginTop: "16px"}}>
                                <Column>
                                    <Button color="warning" isFullWidth disabled={!!missing()}
                                            onClick={() => setStep("confirm")}>Continue</Button>
                                    {missing() ? <p className="help is-danger">{missing()}</p> : null}
                                </Column>
                                <Column>
                                    <Button color="info" isFullWidth onClick={close}>Cancel</Button>
                                </Column>
                            </Columns>
                        </>
                        : null}

                    {form && step === "confirm" ?
                        <>
                            <Box className="has-background-warning-light">
                                <p><strong>This will book a FedEx shipment and print the label.</strong></p>
                                <p>FedEx charges for every shipment booked, so only continue when the parcel is packed and ready to go.</p>
                                {replace ?
                                    <p style={{marginTop: "8px"}}>This order already has label {partOrder.fedex_tracking}. Booking a new one is a second chargeable shipment - cancel the old one with FedEx.</p>
                                    : null}
                                <p style={{marginTop: "8px"}}>
                                    {form.name}{form.company ? ", " + form.company : ""} - {form.street}, {form.city}, {form.postcode}<br/>
                                    {serviceLabel(form.service_type)} - {form.boxes.length} box{form.boxes.length > 1 ? "es" : ""}, {totalKg.toFixed(1)}kg in total
                                </p>
                                <p style={{marginTop: "8px"}}>Do you want to continue?</p>
                            </Box>
                            <Columns>
                                <Column>
                                    <Button color="danger" isFullWidth disabled={busy} onClick={book}>
                                        {busy ? "Booking..." : "Yes - book and print"}
                                    </Button>
                                </Column>
                                <Column>
                                    <Button color="info" isFullWidth disabled={busy} onClick={() => setStep("form")}>No - go back</Button>
                                </Column>
                            </Columns>
                        </>
                        : null}

                    {step === "done" && result ?
                        <>
                            <p className="notification is-success is-light">
                                FedEx label booked - tracking {result.fedex_tracking}.<br/>
                                {result.printed.how === "printed"
                                    ? "Sent to " + (result.printed.printer.name || result.printed.printer.ip) + "."
                                    : result.printed.reason + " The label has downloaded as a .zpl file instead - use Reprint once the printer is sorted."}
                            </p>
                            <Button color="info" isFullWidth onClick={close}>Close</Button>
                        </>
                        : null}
                </Section>
            </Modal.Content>
        </Modal>
    );
}
